import { forwardRef } from "react";
import { SelectInstance } from "react-select";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";

import { useId } from "../../hooks/useId";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { InputContainer } from "../shared";
import { getStyleModeSafeProps } from "../utils/getStyleModeSafeProps";
import { DropdownProps } from "./Dropdown";
import { OptionItem } from "./OptionItem";
import { Placeholder } from "./Placeholder";
import { SharedDropdown } from "./SharedDropdown";

import tokenStyles from "./styles.module.css";

const useResolvedDropdownStyles = createComponentStyleHook<InputProps>({
  legacyStyles: () => import("./styles.module.scss"),
  tokenStyles,
  componentTokens: "dropdown",
});

export interface InputProps extends DropdownProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

export const Input = forwardRef<SelectInstance<OptionItem | null, boolean>, InputProps>(function Input(props, ref) {
  const { label, instructions, tooltip, required = false, children, hideInstructions, hideLabel } = props;
  const { className: _className, style: _style, testId: _testId, ...sharedDropdownProps } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedDropdownStyles(getStyleModeSafeProps(props));

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Dropdown, "nu-dropdown"], // TODO: rename this to "dropdown" when the global style that's breaking things is gone.
    testIdPrefix: "dropdown",
  });

  const id = useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder />}>
        <InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={id}
          label={label}
          instructions={instructions}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          required={required}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <SharedDropdown {...sharedDropdownProps} id={id} ref={ref}>
            {children}
          </SharedDropdown>
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
